import * as React from 'react'

import { IPermitTypeField } from '~/client/graph'
import { IResourceProperty } from '~/client/src/shared/components/ResourceCardList/types'
import { useCleanStaleDefaults } from '~/client/src/shared/components/ResourcePicker/useCleanStaleDefaults'
import { useResourceFieldOptions } from '~/client/src/shared/components/ResourcePicker/useResourceFieldOptions'
import SortOrder from '~/client/src/shared/enums/SortOrder'
import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'

import CollapsibleSectionHeader from './compound/CollapsibleSectionHeader'
import DefaultFilter from './compound/DefaultFilter'
import DefaultGroupBy from './compound/DefaultGroupBy'
import DefaultSort from './compound/DefaultSort'
import DefaultsSection from './compound/DefaultsSection'
import SectionLabel from './compound/SectionLabel'
import FieldToggle from './compound/SelectExistingToggle'
import {
  ResourceDefaultsPopupType,
  ResourceFieldContext,
} from './ResourceFieldContext'
import { useResourceFieldDefaults } from './useResourceFieldDefaults'

interface IProps {
  field: IPermitTypeField
  resourceTypeId: string
  onChange: (updatedField: IPermitTypeField) => void
  getFilterValues?: (key: string) => string[]
  records?: IResourceRecord[]
  properties?: IResourceProperty[]
  propertiesReady: boolean
  children: React.ReactNode
}

const ResourceFieldConfiguratorRoot: React.FC<IProps> = ({
  field,
  resourceTypeId,
  onChange,
  getFilterValues,
  records,
  properties,
  propertiesReady,
  children,
}) => {
  const {
    defaults,
    applyDefaults,
    setSortDefaults,
    setGroupByDefault,
    setFilterDefaults,
    setAllDefaults,
  } = useResourceFieldDefaults(field, onChange)
  const [activePopup, setActivePopup] =
    React.useState<ResourceDefaultsPopupType>(null)

  const { sortKey, sortOrder, groupByKey, filterSelection } = defaults
  const hasSortDefaults = sortKey !== null
  const hasGroupByDefaults = groupByKey !== null
  const hasFilterDefaults = filterSelection.length > 0

  const togglePopup = React.useCallback((popup: ResourceDefaultsPopupType) => {
    setActivePopup(prev => (prev === popup ? null : popup))
  }, [])

  const closePopup = React.useCallback(() => setActivePopup(null), [])

  const resetDefaults = React.useCallback(() => {
    setAllDefaults(null, SortOrder.ASC, null, [])
  }, [setAllDefaults])

  // --- Resource field options ---
  const { sortOptions, groupByOptions, filterCategories } =
    useResourceFieldOptions({
      resourceTypeId,
      properties,
      getFilterValues,
      records,
    })

  useCleanStaleDefaults({
    propertiesReady,
    sortOptions,
    groupByOptions,
    filterCategories,
    defaults,
    onChange: applyDefaults,
  })

  const ctx = React.useMemo(
    () => ({
      field,
      resourceTypeId,
      onChange,
      sortOptions,
      groupByOptions,
      filterCategories,
      sortKey,
      sortOrder,
      groupByKey,
      filterSelection,
      activePopup,
      hasSortDefaults,
      hasGroupByDefaults,
      hasFilterDefaults,
      setSortDefaults,
      setGroupByDefault,
      setFilterDefaults,
      setAllDefaults,
      togglePopup,
      closePopup,
      resetDefaults,
    }),
    [
      field,
      resourceTypeId,
      onChange,
      sortOptions,
      groupByOptions,
      filterCategories,
      sortKey,
      sortOrder,
      groupByKey,
      filterSelection,
      activePopup,
      hasSortDefaults,
      hasGroupByDefaults,
      hasFilterDefaults,
      setSortDefaults,
      setGroupByDefault,
      setFilterDefaults,
      setAllDefaults,
      togglePopup,
      closePopup,
      resetDefaults,
    ],
  )

  return (
    <ResourceFieldContext.Provider value={ctx}>
      <div className="tw:flex tw:flex-col tw:pl-6">{children}</div>
    </ResourceFieldContext.Provider>
  )
}

const ResourceFieldConfigurator = Object.assign(ResourceFieldConfiguratorRoot, {
  FieldToggle,
  SectionLabel,
  CollapsibleSectionHeader,
  DefaultSort,
  DefaultGroupBy,
  DefaultFilter,
  DefaultsSection,
})

export default ResourceFieldConfigurator
